import type { TFunction } from 'i18next';
import type { OnboardingImportItem } from '../../../api/account/accountApiTypes';
import type { CustomerOrganizationType } from '../../../api/organization/organizationApiTypes';
import { CUSTOMER_ORGANIZATION_TYPES } from '../../../api/organization/organizationApiTypes';
import { isUnanswered } from '../../../components/importReport/importReportView';
import type {
    OrganizationImportArgs,
    OrganizationImportCsv,
    OrganizationImportFile,
    OrganizationImportInvite,
    OrganizationImportReport,
    OrganizationImportTypeSource,
    OrganizationInviteSendState,
    SendOrganizationImportInvitesResult,
} from '../../../api/organization/organizationImportTypes';
import type { ImportGroup } from '../../../components/importReport/importReportView';
import { OpenPoint, point } from '../../../components/importReport/openPoints';

/**
 * "Organisationen importieren" (plan docs/plans/organization-import.md §5.3):
 * what the page decides itself. What an item, an issue or an open point
 * reads as is shared with "Einrichtung per Datei" (`components/importReport/`).
 */

/** The file input: a CSV export, a kolula file, or either saved as ".txt" (Windows, Teams). */
export const ORGANIZATION_FILE_ACCEPT = '.csv,.json,.txt,text/csv,application/json,text/plain';

export type ImportSource = 'csv' | 'json';

/**
 * The format the server reads the file as: its name decides for ".csv" and
 * ".json"; any other name (".txt") is read as the source the tenant chose.
 */
export const formatOf = (fileName: string, source: ImportSource): ImportSource => {
    const name = fileName.trim().toLowerCase();
    if (name.endsWith('.csv')) return 'csv';
    if (name.endsWith('.json')) return 'json';
    return source;
};

/**
 * The preview's groups (§5.3): Organisationen · Einheiten · Ansprechpartner ·
 * Verträge · Räume und Belegungszeiten — a room and its fixed times side by
 * side — and before them the customer classes a kolula file may bring.
 */
export const ORGANIZATION_IMPORT_GROUPS: readonly ImportGroup[] = [
    { group: 'customerClass', sections: ['customerClass'] },
    { group: 'organization', sections: ['organization'] },
    { group: 'unit', sections: ['unit'] },
    { group: 'contact', sections: ['contact'] },
    { group: 'contract', sections: ['contract'] },
    { group: 'rooms', sections: ['access', 'window'] },
    { group: 'manual', sections: ['manual'] },
    { group: 'file', sections: ['file'] },
];

/** Where a row's type comes from as the page shows it: the server's sources, or chosen here in the dropdown. */
export type TypeSource = OrganizationImportTypeSource | 'chosen';

export const isOrganizationType = (value: unknown): value is CustomerOrganizationType =>
    typeof value === 'string' && (CUSTOMER_ORGANIZATION_TYPES as readonly string[]).includes(value);

/** The file's organisation of this legal name (the item's key is the file's `legalName`, exactly). */
const fileOrganization = (file: OrganizationImportFile | undefined, legalName: string) =>
    (file?.organizations ?? []).find((o) => o.legalName === legalName);

/** The type a row has now: chosen here, else the file's. Null when it has none. */
export const typeOf = (
    file: OrganizationImportFile | undefined,
    overrides: Readonly<Record<string, CustomerOrganizationType>>,
    legalName: string,
): CustomerOrganizationType | null => {
    const chosen = overrides[legalName];
    if (chosen) return chosen;
    const type = fileOrganization(file, legalName)?.type;
    return isOrganizationType(type) ? type : null;
};

/** Whether the row can take a type from the dropdown: it is an organisation of the file the preview returned. */
export const hasTypeRow = (file: OrganizationImportFile | undefined, item: OnboardingImportItem): boolean =>
    item.section === 'organization' && fileOrganization(file, item.key) !== undefined;

/**
 * Where a row's type comes from: chosen here; else the server's answer for a
 * CSV (`detail.typeSource`); else, for a kolula file, "aus Datei" when it has
 * one. Null: no type at all (the row is not created without one).
 */
export const typeSourceOf = (
    item: OnboardingImportItem,
    file: OrganizationImportFile | undefined,
    overrides: Readonly<Record<string, CustomerOrganizationType>>,
    csv: OrganizationImportCsv | undefined,
): TypeSource | null => {
    if (overrides[item.key]) return 'chosen';
    // The server's answer for a sheet's row; an edited file's answer has none, so the sheet's own list is asked.
    const fromServer = item.detail?.typeSource;
    if (typeof fromServer === 'string' && fromServer) return fromServer as OrganizationImportTypeSource;
    if (csv) {
        const row = csv.organizations.find((o) => o.legalName === item.key);
        return row?.typeSource ?? null;
    }
    return typeOf(file, overrides, item.key) ? 'file' : null;
};

/** The file with the types chosen here written into it: what the apply sends as `format: json` (§5.1). */
export const editedFile = (
    file: OrganizationImportFile,
    overrides: Readonly<Record<string, CustomerOrganizationType>>,
): OrganizationImportFile => ({
    ...file,
    organizations: (file.organizations ?? []).map((org) => {
        const chosen = typeof org.legalName === 'string' ? overrides[org.legalName] : undefined;
        return chosen ? { ...org, type: chosen } : org;
    }),
});

/** The upload's own preview: what the page edits types in, and what the apply sends while no type was chosen here. */
export interface PreviewBase {
    args: OrganizationImportArgs;
    report: OrganizationImportReport;
}

/** The edited file as the server reads it: JSON, whatever the upload was (§5.1). */
export const editedArgs = (
    file: OrganizationImportFile,
    overrides: Readonly<Record<string, CustomerOrganizationType>>,
): OrganizationImportArgs => ({ fileText: JSON.stringify(editedFile(file, overrides)), format: 'json' });

/** What the apply sends: the upload itself, or — once a type was chosen here — the edited file. Null without a preview. */
export const applyArgsOf = (
    base: PreviewBase | null,
    overrides: Readonly<Record<string, CustomerOrganizationType>>,
): OrganizationImportArgs | null => {
    if (!base) return null;
    if (Object.keys(overrides).length === 0 || !base.report.file) return base.args;
    return editedArgs(base.report.file, overrides);
};

/** The edited file's answer as the page shows it: the sheet's file, columns, notes and type sources stay the upload's. */
export const withUploadSheet = (edited: OrganizationImportReport, upload: OrganizationImportReport): OrganizationImportReport => ({
    ...edited,
    ...(upload.file ? { file: upload.file } : {}),
    ...(upload.csv ? { csv: upload.csv } : {}),
});

/**
 * What a sheet leaves out, as open points (§5.1, §5.3): a column kolula does
 * not take over — unknown, a phone number (§8 Q7), a second column of one
 * field — Optional; a type word that is none (the type was proposed or the
 * default) Optional, the dropdown shows what was taken; a later row's value
 * that differs from the first row's, Wichtig — kolula keeps the first, and
 * which one is right only the tenant knows.
 */
export const csvPoints = (csv: OrganizationImportCsv | undefined, t: TFunction): OpenPoint[] => {
    if (!csv) return [];
    return [
        ...csv.columns.ignored.map((column) => point(
            'column_not_imported',
            t('organization_import.points.column', { header: column.header }),
            t,
            t(`organization_import.points.column_reason.${column.reason}`),
        )),
        ...csv.notes.map((note) => point(
            note.code,
            t('organization_import.points.line', { line: note.line }),
            t,
            t(`organization_import.points.note.${note.code}`, { value: note.value, column: note.column }),
        )),
    ];
};

/** The preview reports a contract only the owner may import: the page says who can bring it in. */
export const needsOwner = (report: OrganizationImportReport | null): boolean =>
    !!report && report.items.some((item) => item.issues.some((issue) => issue.code === 'owner_required'));

/** The CSV refusals the page names in its own words (400 `file_unreadable`, `reason`). */
const CSV_REASONS = new Set(['empty', 'no_name_column', 'no_rows', 'too_many_rows', 'unclosed_quote', 'not_json']);

const ERROR_CODES: Record<string, string> = {
    file_missing: 'organization_import.error.unreadable',
    file_too_large: 'organization_import.error.file_too_large',
    format_invalid: 'organization_import.error.unreadable',
    default_type_invalid: 'organization_import.error.default_type',
    organization_import_unsupported: 'organization_import.error.unsupported',
    not_onboarding_file: 'organization_import.error.not_format',
};

/**
 * A refusal in the page's words, by cause — no answer, a sheet that cannot be
 * read (and why), a file that is not the format, a known code — else the
 * server's own message, never a bare "failed".
 */
export const importErrorText = (err: unknown, t: TFunction, edited = false): string => {
    const e = err as { status?: number, data?: { code?: unknown, reason?: unknown, line?: unknown, message?: unknown } } | undefined;
    if (!e || e.status === undefined) return t('organization_import.error.network');
    const code = typeof e.data?.code === 'string' ? e.data.code : '';
    const reason = typeof e.data?.reason === 'string' ? e.data.reason : '';
    if (code === 'file_unreadable' && CSV_REASONS.has(reason)) {
        return t(`organization_import.error.csv.${reason}`, { line: typeof e.data?.line === 'number' ? e.data.line : '' });
    }
    // The file sent was the one edited here (JSON), not the upload: say which is too large.
    if (code === 'file_too_large' && edited) return t('organization_import.error.edited_too_large');
    if (ERROR_CODES[code]) return t(ERROR_CODES[code] as string);
    const server = typeof e.data?.message === 'string' ? e.data.message : '';
    return t('organization_import.error.generic', { detail: server || t('organization_import.error.status', { status: e.status }) });
};

/**
 * "Einladungen senden" sends this many per call; more go in further calls. Kept well below the
 * route's limit of 500: the server mails one address after another, so a large batch risks the
 * gateway timing out, and an unanswered batch is offered again whole (possible duplicate mails).
 */
export const INVITES_PER_CALL = 25;

export const inviteBatches = (ids: string[]): string[][] => {
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += INVITES_PER_CALL) batches.push(ids.slice(i, i + INVITES_PER_CALL));
    return batches;
};

/** An invitation's state on the result screen: not sent yet, or what the send answered. */
export type InviteState = 'pending' | 'sending' | OrganizationInviteSendState;

/** What "Einladungen senden" still has to send: every invitation not sent and not gone. */
export const invitesToSend = (invites: OrganizationImportInvite[], states: Readonly<Record<string, InviteState>>): string[] =>
    invites
        .filter((invite) => {
            const state = states[invite.inviteId] ?? 'pending';
            return state === 'pending' || state === 'failed' || state === 'skipped_no_app_url';
        })
        .map((invite) => invite.inviteId);

export type InviteStates = Readonly<Record<string, InviteState>>;

const withState = (states: InviteStates, ids: string[], state: InviteState): InviteStates =>
    ({ ...states, ...Object.fromEntries(ids.map((id) => [id, state])) });

/** How the last call of "Einladungen senden" failed: refused, or not answered (some mails may have gone out). */
export interface InviteSendFailure {
    error: unknown;
    unanswered: boolean;
}

/**
 * "Einladungen senden" in batches of `INVITES_PER_CALL`, one after another.
 * A batch is `sending` only while its own call runs; its answer sets each
 * invitation's state. A failed call puts its batch back to `pending` and
 * stops: later batches were never sent and stay `pending`, so all of them
 * are offered again — nothing is left at `sending`.
 */
export const sendInviteBatches = async (
    ids: string[],
    send: (batch: string[]) => Promise<SendOrganizationImportInvitesResult>,
    update: (change: (states: InviteStates) => InviteStates) => void,
): Promise<InviteSendFailure | null> => {
    for (const batch of inviteBatches(ids)) {
        update((states) => withState(states, batch, 'sending'));
        try {
            const answer = await send(batch);
            update((states) => ({ ...states, ...Object.fromEntries(answer.results.map((r) => [r.inviteId, r.state])) }));
        } catch (error) {
            update((states) => withState(states, batch, 'pending'));
            return { error, unanswered: isUnanswered(error) };
        }
    }
    return null;
};
